/**
 * EditTagsAndNotesDialog — operator-only labels + pinned internal
 * note. Phase C (migration 0030). Tags are stored as a text[]; the
 * dialog replaces the set wholesale on save (no add/remove
 * granularity yet). Internal note is a free-form text column;
 * empty input clears it.
 *
 * Neither field is ever shown to the customer or printed on
 * invoices — this is the team's scratchpad.
 *
 * @see prototype/sales/customer-new.html §7 — visual baseline
 * @see packages/db/migrations/0030_customer_phase_c.sql
 */
import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@flint/ui";
import type { customers as contract } from "@flint/api-contract";
import type { z } from "zod";
import { EditDialog } from "./edit-dialog.tsx";
import { SectionField } from "./section-field.tsx";

type CustomerRow = z.infer<typeof contract.customerRow>;

export interface EditTagsAndNotesDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  customer: CustomerRow;
  pending: boolean;
  serverError: string | null;
  onSave: (patch: { tags?: string[]; internalNote?: string }) => Promise<void>;
}

const TAG_MAX_LENGTH = 40;
const TAGS_MAX_COUNT = 20;
const NOTE_MAX_LENGTH = 2000;

export function EditTagsAndNotesDialog({
  open,
  onOpenChange,
  customer,
  pending,
  serverError,
  onSave,
}: EditTagsAndNotesDialogProps) {
  const [tags, setTags] = React.useState<string[]>(customer.tags);
  const [tagDraft, setTagDraft] = React.useState("");
  const [internalNote, setInternalNote] = React.useState(customer.internalNote ?? "");

  React.useEffect(() => {
    if (open) {
      setTags(customer.tags);
      setTagDraft("");
      setInternalNote(customer.internalNote ?? "");
    }
  }, [open, customer]);

  const addTagFromDraft = () => {
    const next = tagDraft.trim();
    if (!next) return;
    if (next.length > TAG_MAX_LENGTH) return;
    if (tags.length >= TAGS_MAX_COUNT) return;
    if (tags.includes(next)) {
      setTagDraft("");
      return;
    }
    setTags((current) => [...current, next]);
    setTagDraft("");
  };

  const removeTag = (target: string) => {
    setTags((current) => current.filter((t) => t !== target));
  };

  const onTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTagFromDraft();
    } else if (e.key === "Backspace" && tagDraft === "" && tags.length > 0) {
      // Quick-delete the last tag if the input is empty.
      setTags((current) => current.slice(0, -1));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Flush any in-progress draft so Save also commits the typed tag.
    const flushed = tagDraft.trim();
    const finalTags =
      flushed && !tags.includes(flushed) && tags.length < TAGS_MAX_COUNT
        ? [...tags, flushed]
        : tags;

    const patch: Parameters<EditTagsAndNotesDialogProps["onSave"]>[0] = {};

    const tagsChanged =
      finalTags.length !== customer.tags.length || finalTags.some((t, i) => t !== customer.tags[i]);
    if (tagsChanged) patch.tags = finalTags;

    const trimmedNote = internalNote.trim();
    if (trimmedNote !== (customer.internalNote ?? "")) {
      patch.internalNote = trimmedNote;
    }

    if (Object.keys(patch).length === 0) {
      onOpenChange(false);
      return;
    }
    await onSave(patch);
  };

  return (
    <EditDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Tags & internal notes"
      description="For your team only — never shared with the customer or shown on invoices."
      pending={pending}
      serverError={serverError}
      onSubmit={handleSubmit}
      maxWidthClass="max-w-lg"
    >
      <SectionField
        label="Tags"
        hint={
          tags.length >= TAGS_MAX_COUNT
            ? `Maximum ${TAGS_MAX_COUNT} tags per customer.`
            : "Press Enter or comma to add. Used for filters, segments, AI grouping in briefings."
        }
      >
        <div
          className={cn(
            "flex flex-wrap items-center gap-1.5 min-h-[40px] rounded-md border border-line bg-paper px-2 py-1.5",
            "focus-within:border-sky-500 focus-within:ring-2 focus-within:ring-sky-100",
          )}
        >
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-sm bg-sky-50 border border-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-700"
            >
              {tag}
              <button
                type="button"
                aria-label={`Remove tag ${tag}`}
                onClick={() => removeTag(tag)}
                className="inline-grid place-items-center w-3.5 h-3.5 rounded-sm text-sky-700/70 hover:text-sky-900 hover:bg-sky-100"
              >
                <X size={9} strokeWidth={2.4} aria-hidden />
              </button>
            </span>
          ))}
          <input
            type="text"
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value.slice(0, TAG_MAX_LENGTH))}
            onKeyDown={onTagKeyDown}
            onBlur={addTagFromDraft}
            placeholder={tags.length === 0 ? "Add tag…" : ""}
            disabled={tags.length >= TAGS_MAX_COUNT}
            className="flex-1 min-w-[8ch] bg-transparent outline-none text-sm text-ink placeholder:text-muted disabled:cursor-not-allowed"
          />
        </div>
      </SectionField>

      <SectionField
        label="Internal note"
        optional="optional"
        hint={`${internalNote.length} / ${NOTE_MAX_LENGTH} chars. Anything the team should know about this customer.`}
      >
        <textarea
          value={internalNote}
          onChange={(e) => setInternalNote(e.target.value.slice(0, NOTE_MAX_LENGTH))}
          rows={5}
          placeholder="e.g., Pay slow but reliable — always ask for PO copy in advance."
          className={cn(
            "w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink resize-y",
            "placeholder:text-muted",
            "focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100",
          )}
        />
      </SectionField>
    </EditDialog>
  );
}
